import { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FormatPaintRounded from '@mui/icons-material/FormatPaintRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import PublishRounded from '@mui/icons-material/PublishRounded';
import UnpublishedRounded from '@mui/icons-material/UnpublishedRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import exportRowsToXlsx from '../utils/exportXlsx';
import ImageUploader from '../components/common/ImageUploader';
import PushResultsDialog from '../components/common/PushResultsDialog';
import { useStores } from '../context/StoresContext';
import {
  getFinishGroups, createFinishGroup, updateFinishGroup, deleteFinishGroup,
  getFinishTypes, getFinishType, createFinishType, updateFinishType, deleteFinishType,
  updateTypeManufacturers, updateTypeFeatures,
  getFinishColors, createFinishColor, updateFinishColor, deleteFinishColor,
  bulkUpdateTypeStoreStatus, updateTypeStoreStatus, bulkDeleteTypes, bulkDeleteGroups,
  searchShopifyForType, pushFinishType, matchFinishType, unmatchFinishType, updateFinishTypeShopifyStatus,
  importFinishes, exportFinishesJson,
} from '../services/finishesApi';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import { getManufacturers } from '../services/manufacturersApi';
import { getFeatures } from '../services/featuresApi';

const Finishes = () => {
  const { activeStores } = useStores();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);

  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [colorsByType, setColorsByType] = useState({});

  const [groupDialog, setGroupDialog] = useState(false);
  const [typeDialog, setTypeDialog] = useState({ open: false, groupId: null });
  const [colorDialog, setColorDialog] = useState({ open: false, typeId: null });
  const [editDialog, setEditDialog] = useState({ open: false, type: null });
  const [editGroupDialog, setEditGroupDialog] = useState({ open: false, group: null });
  const [groupEditName, setGroupEditName] = useState('');
  const [editColorDialog, setEditColorDialog] = useState({ open: false, color: null, typeId: null });

  const [formName, setFormName] = useState('');
  const [formImage, setFormImage] = useState('');
  const [formColorCode, setFormColorCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [importing, setImporting] = useState(false);

  const [editForm, setEditForm] = useState({});
  const [editMfrs, setEditMfrs] = useState([]);
  const [editFeatures, setEditFeatures] = useState([]);
  const [editStoreStatuses, setEditStoreStatuses] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Shopify matching
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchStoreId, setMatchStoreId] = useState(null);
  const [matchTypeId, setMatchTypeId] = useState(null);
  const [matchQuery, setMatchQuery] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [matchSearching, setMatchSearching] = useState(false);
  const [matchingProduct, setMatchingProduct] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // ─── Filter state ───
  const [searchQuery, setSearchQuery] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState(null);
  const [filterStore, setFilterStore] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  // ─── Selection state ───
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [bulkStore, setBulkStore] = useState('');
  const [bulkActioning, setBulkActioning] = useState(false);
  const [pushResults, setPushResults] = useState({ open: false, results: [] });

  const fetchAll = useCallback(async () => {
    try {
      const [groupsData, typesData, mfrsData, featuresData] = await Promise.all([
        getFinishGroups(), getFinishTypes(), getManufacturers(), getFeatures(),
      ]);
      setGroups(groupsData.items);
      setTypes(typesData.items);
      setAllFeatures(featuresData.items || []);
      setAllManufacturers(mfrsData.items);
    } catch (err) {
      console.error('Failed to fetch finishes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleGroup = (groupId) => setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));

  const toggleType = async (typeId) => {
    const isExpanding = !expandedTypes[typeId];
    setExpandedTypes((prev) => ({ ...prev, [typeId]: !prev[typeId] }));
    if (isExpanding && !colorsByType[typeId]) {
      try {
        const data = await getFinishColors(typeId);
        setColorsByType((prev) => ({ ...prev, [typeId]: data.items }));
      } catch (err) { console.error('Failed to fetch colors:', err); }
    }
  };

  // ─── Filter logic ───
  const filteredTypeIds = useMemo(() => {
    const ids = new Set();
    for (const t of types) {
      // search filter
      if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) continue;
      // manufacturer filter
      if (filterManufacturer && !t.manufacturers.some((m) => m.id === filterManufacturer.id)) continue;
      // store + status filter
      if (filterStore) {
        const storeStatus = (t.store_statuses || []).find((s) => s.store_id === Number(filterStore));
        const currentStatus = storeStatus ? storeStatus.status : 'inactive';
        if (filterStatus === 'Published' && currentStatus !== 'active') continue;
        if (filterStatus === 'Unpublished' && currentStatus !== 'inactive') continue;
      }
      ids.add(t.id);
    }
    return ids;
  }, [types, searchQuery, filterManufacturer, filterStore, filterStatus]);

  const typesForGroup = useCallback((groupId) => {
    return types.filter((t) => t.finish_group_id === groupId && filteredTypeIds.has(t.id));
  }, [types, filteredTypeIds]);

  // ─── Visible type IDs (for select all) ───
  const visibleTypeIds = useMemo(() => {
    return types.filter((t) => filteredTypeIds.has(t.id)).map((t) => t.id);
  }, [types, filteredTypeIds]);

  const allVisibleSelected = visibleTypeIds.length > 0 && visibleTypeIds.every((id) => selectedTypes.has(id));

  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedTypes(new Set());
    } else {
      setSelectedTypes(new Set(visibleTypeIds));
    }
  };

  const handleToggleSelect = (typeId) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  // ─── Bulk actions ───
  const handleBulkAction = async (status) => {
    if (!bulkStore || selectedTypes.size === 0) return;
    setBulkActioning(true);
    try {
      await bulkUpdateTypeStoreStatus(Array.from(selectedTypes), Number(bulkStore), status);
      setSelectedTypes(new Set());
      await fetchAll();
    } catch (err) { console.error('Bulk action failed:', err); }
    finally { setBulkActioning(false); }
  };

  const handleBulkPush = async () => {
    if (!bulkStore || selectedTypes.size === 0) return;
    const storeId = Number(bulkStore);
    const store = activeStores.find((s) => s.id === storeId);
    const selectedList = Array.from(selectedTypes)
      .map((id) => types.find((t) => t.id === id))
      .filter(Boolean);
    const existing = selectedList.filter((t) => (t.pushed_counts?.[storeId] || 0) > 0);
    if (existing.length > 0) {
      const names = existing.slice(0, 5).map((t) => t.name).join(', ');
      const more = existing.length > 5 ? `, +${existing.length - 5} more` : '';
      const totalColors = existing.reduce((sum, t) => sum + (t.pushed_counts?.[storeId] || 0), 0);
      if (!window.confirm(
        `${existing.length} finish type(s) already live on ${storeLabel(store) || 'this store'} (${totalColors} Shopify product(s) total): ${names}${more}.\n\n` +
        `Pushing will OVERRIDE the existing Shopify products and metafields. Continue?`
      )) return;
    }
    setBulkActioning(true);
    const results = [];
    try {
      const ids = Array.from(selectedTypes);
      for (const typeId of ids) {
        const t = types.find((x) => x.id === typeId);
        try {
          const data = await pushFinishType(typeId, storeId);
          const errors = data?.errors || [];
          const products = data?.products || [];
          results.push({
            name: t?.name || `Type ${typeId}`,
            status: errors.length === 0 ? 'ok' : (products.length > 0 ? 'partial' : 'error'),
            message: data?.message,
            products,
            errors,
          });
        } catch (err) {
          results.push({
            name: t?.name || `Type ${typeId}`,
            status: 'error',
            message: err.response?.data?.message || err.message,
            errors: [{ field: 'request', message: err.response?.data?.detail || err.message }],
          });
        }
      }
      setSelectedTypes(new Set());
      await fetchAll();
      setPushResults({ open: true, results });
    } catch (err) { console.error('Bulk push failed:', err); }
    finally { setBulkActioning(false); }
  };

  const handleBulkDelete = async () => {
    if (selectedTypes.size === 0) return;
    if (!window.confirm(`Delete ${selectedTypes.size} selected type(s) and all their colors? This cannot be undone.`)) return;
    setBulkActioning(true);
    try {
      await bulkDeleteTypes(Array.from(selectedTypes));
      setSelectedTypes(new Set());
      await fetchAll();
    } catch (err) { console.error('Bulk delete failed:', err); }
    finally { setBulkActioning(false); }
  };

  // Selecting a group cascades to all its types so the bulk Push / Status /
  // Delete actions (which operate on selectedTypes) act on the whole group.
  const handleToggleGroupSelect = (groupId) => {
    const groupTypeIds = typesForGroup(groupId).map((t) => t.id);
    const allSelected = groupTypeIds.length > 0 && groupTypeIds.every((id) => selectedTypes.has(id));
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (allSelected) groupTypeIds.forEach((id) => next.delete(id));
      else groupTypeIds.forEach((id) => next.add(id));
      return next;
    });
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (allSelected) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };
  const handleToggleSelectAllGroups = () => {
    const allSelected = groups.length > 0 && groups.every((g) => selectedGroups.has(g.id));
    if (allSelected) {
      setSelectedGroups(new Set());
      setSelectedTypes(new Set());
    } else {
      setSelectedGroups(new Set(groups.map((g) => g.id)));
      setSelectedTypes(new Set(visibleTypeIds));
    }
  };
  const handleBulkDeleteGroups = async () => {
    if (selectedGroups.size === 0) return;
    if (!window.confirm(`Delete ${selectedGroups.size} selected group(s) and ALL their types/colors? This cannot be undone.`)) return;
    setBulkActioning(true);
    try {
      await bulkDeleteGroups(Array.from(selectedGroups));
      setSelectedGroups(new Set());
      setSelectedTypes(new Set());
      await fetchAll();
    } catch (err) { console.error('Bulk delete groups failed:', err); }
    finally { setBulkActioning(false); }
  };

  // ─── Group CRUD ───
  const handleCreateGroup = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await createFinishGroup(formName.trim());
      setGroupDialog(false); setFormName('');
      await fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const openEditGroup = (group) => {
    setGroupEditName(group.name);
    setError('');
    setEditGroupDialog({ open: true, group });
  };
  const handleSaveGroupEdit = async () => {
    if (!groupEditName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await updateFinishGroup(editGroupDialog.group.id, { name: groupEditName.trim() });
      setEditGroupDialog({ open: false, group: null });
      await fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to rename group'); }
    finally { setSaving(false); }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Delete this finish group and all its types/colors?')) return;
    setDeleting(`group-${id}`);
    try { await deleteFinishGroup(id); await fetchAll(); }
    catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  // ─── Type CRUD ───
  const handleCreateType = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await createFinishType({ finish_group_id: typeDialog.groupId, name: formName.trim() });
      setTypeDialog({ open: false, groupId: null }); setFormName('');
      await fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Delete this finish type and all its colors?')) return;
    setDeleting(`type-${id}`);
    try { await deleteFinishType(id); await fetchAll(); }
    catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  // Shopify matching handlers
  const openMatchForType = (typeId, storeId) => {
    setMatchTypeId(typeId);
    setMatchStoreId(storeId);
    setMatchQuery('');
    setMatchResults([]);
    setMatchDialogOpen(true);
  };

  const handleMatchSearch = async () => {
    if (!matchQuery.trim() || !matchStoreId) return;
    setMatchSearching(true);
    try {
      const data = await searchShopifyForType(matchStoreId, matchQuery);
      setMatchResults(data.products || []);
    } catch (err) { console.error('Search failed:', err); }
    finally { setMatchSearching(false); }
  };

  const handleMatch = async (shopifyProductId) => {
    setMatchingProduct(true);
    try {
      await matchFinishType(matchTypeId, matchStoreId, shopifyProductId);
      setMatchDialogOpen(false);
      const data = await getFinishType(matchTypeId);
      const statuses = {};
      for (const s of data.item.store_statuses || []) statuses[s.store_id] = s;
      setEditStoreStatuses(statuses);
      await fetchAll();
    } catch (err) { console.error('Match failed:', err); }
    finally { setMatchingProduct(false); }
  };

  const handleUnmatch = async (typeId, storeId) => {
    if (!window.confirm('Unlink this type from this store?')) return;
    try {
      await unmatchFinishType(typeId, storeId);
      const data = await getFinishType(typeId);
      const statuses = {};
      for (const s of data.item.store_statuses || []) statuses[s.store_id] = s;
      setEditStoreStatuses(statuses);
      await fetchAll();
    } catch (err) { console.error('Unmatch failed:', err); }
  };

  // List-row version: same endpoint but refetches the whole types list
  // so the chip-replaced Select on every row picks up the new value.
  const handleShopifyStatusChangeForRow = async (typeId, storeId, newStatus) => {
    setUpdatingStatus(`${typeId}-${storeId}`);
    try {
      await updateFinishTypeShopifyStatus(typeId, storeId, newStatus);
      await fetchAll();
    } catch (err) { console.error('Type status change failed:', err); }
    finally { setUpdatingStatus(null); }
  };

  const handleShopifyStatusChange = async (typeId, storeId, newStatus) => {
    setUpdatingStatus(storeId);
    try {
      await updateFinishTypeShopifyStatus(typeId, storeId, newStatus);
      const data = await getFinishType(typeId);
      const statuses = {};
      for (const s of data.item.store_statuses || []) statuses[s.store_id] = s;
      setEditStoreStatuses(statuses);
    } catch (err) { console.error('Status update failed:', err); }
    finally { setUpdatingStatus(null); }
  };

  const openEditType = async (typeId) => {
    try {
      const data = await getFinishType(typeId);
      const t = data.item;
      setEditForm({
        name: t.name,
        internal_reference: t.internal_reference || '',
        finish_group_id: t.finish_group_id,
        status: t.status || 'ready',
      });
      setEditMfrs(t.manufacturers.map((m) => m.id));
      setEditFeatures((t.features || []).map((f) => f.id));
      const statuses = {};
      for (const s of t.store_statuses || []) {
        statuses[s.store_id] = s;
      }
      setEditStoreStatuses(statuses);
      setEditDialog({ open: true, type: t });
    } catch (err) { console.error('Failed to load type:', err); }
  };

  const handleSaveEdit = async () => {
    if (!editDialog.type) return;
    setSavingEdit(true);
    try {
      const tid = editDialog.type.id;
      await updateFinishType(tid, editForm);
      await updateTypeManufacturers(tid, editMfrs);
      await updateTypeFeatures(tid, editFeatures);
      const storeStatusesArr = Object.entries(editStoreStatuses).map(([store_id, val]) => ({
        store_id: Number(store_id), status: typeof val === 'string' ? val : val.status,
      }));
      if (storeStatusesArr.length > 0) await updateTypeStoreStatus(tid, storeStatusesArr);
      setEditDialog({ open: false, type: null });
      await fetchAll();
    } catch (err) { console.error('Save failed:', err); }
    finally { setSavingEdit(false); }
  };

  // ─── Color CRUD ───
  const handleCreateColor = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const tid = colorDialog.typeId;
    try {
      await createFinishColor({ finish_type_id: tid, name: formName.trim(), image_url: formImage.trim(), color_code: formColorCode.trim() });
      setColorDialog({ open: false, typeId: null }); setFormName(''); setFormImage(''); setFormColorCode('');
      const data = await getFinishColors(tid);
      setColorsByType((prev) => ({ ...prev, [tid]: data.items }));
      await fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const handleDeleteColor = async (id, typeId) => {
    if (!window.confirm('Delete this color?')) return;
    setDeleting(`color-${id}`);
    try {
      await deleteFinishColor(id);
      const data = await getFinishColors(typeId);
      setColorsByType((prev) => ({ ...prev, [typeId]: data.items }));
      await fetchAll();
    } catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  const openEditColor = (color, typeId) => {
    setEditColorDialog({ open: true, color, typeId });
    setFormName(color.name);
    setFormImage(color.image_url || '');
    setFormColorCode(color.color_code || '');
    setError('');
  };

  const handleSaveColor = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const { color, typeId } = editColorDialog;
    try {
      await updateFinishColor(color.id, { name: formName.trim(), image_url: formImage.trim(), color_code: formColorCode.trim() });
      setEditColorDialog({ open: false, color: null, typeId: null });
      setFormName(''); setFormImage(''); setFormColorCode('');
      const data = await getFinishColors(typeId);
      setColorsByType((prev) => ({ ...prev, [typeId]: data.items }));
    } catch (err) { setError(err.response?.data?.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  // ─── Helper: get store status for a type ───
  const getTypeStoreStatus = (type, storeId) => {
    const ss = (type.store_statuses || []).find((s) => s.store_id === storeId);
    return ss ? ss.status : 'inactive';
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Finishes" subtitle="Manage finish groups, types, and colors" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <input
        id="finishes-import-file"
        type="file"
        accept=".xlsx,.xls,.csv,.tsv"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setImporting(true);
          try {
            const XLSX = await import('xlsx');
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer);
            const ws = wb.Sheets[wb.SheetNames[0]];
            const allRows = XLSX.utils.sheet_to_json(ws, { header: 1 });
            if (allRows.length < 2) throw new Error('No data rows');
            // Find header row: look for a row containing "_id" or "Name" + "Group"
            let headerIdx = 0;
            for (let i = 0; i < Math.min(allRows.length, 5); i++) {
              const firstCell = String(allRows[i]?.[0] || '').toLowerCase();
              const rowLower = (allRows[i] || []).map((c) => String(c || '').toLowerCase());
              if (firstCell === '_id' || (rowLower.includes('name') && rowLower.includes('group'))) {
                headerIdx = i; break;
              }
            }
            const headers = (allRows[headerIdx] || []).map((h) => String(h || '').toLowerCase().trim());
            console.log('Finishes import - Header row index:', headerIdx, 'Headers:', headers);
            // Map Excel headers to backend field names
            const HEADER_MAP = {
              '_id': '_id', 'handle': '_id',
              'product type': 'product type', 'type': 'product type',
              'name': 'name', 'product name': 'name',
              'group': 'group', 'group_name': 'group',
              'image': 'image', 'image src': 'image', 'image_url': 'image',
              'color code': 'color_code', 'color_code': 'color_code', 'colorcode': 'color_code',
            };
            const items = [];
            for (let i = headerIdx + 1; i < allRows.length; i++) {
              const obj = {};
              headers.forEach((h, idx) => {
                const key = HEADER_MAP[h] || h;
                obj[key] = String(allRows[i]?.[idx] ?? '').trim();
              });
              items.push(obj);
            }
            console.log('Finishes import - First item:', items[0], 'Total items:', items.length);
            const result = await importFinishes(items);
            const r = result.results;
            const total = (r.created_groups || 0) + (r.created_types || 0) + (r.created_colors || 0) + (r.updated_colors || 0);
            const parts = [];
            if (r.created_groups) parts.push(`${r.created_groups} new group${r.created_groups !== 1 ? 's' : ''}`);
            if (r.created_types) parts.push(`${r.created_types} new type${r.created_types !== 1 ? 's' : ''}`);
            if (r.created_colors) parts.push(`${r.created_colors} new color${r.created_colors !== 1 ? 's' : ''}`);
            if (r.updated_colors) parts.push(`${r.updated_colors} updated`);
            if (r.skipped) parts.push(`${r.skipped} skipped`);
            alert(total > 0 ? `Import complete! ${parts.join(', ')}` : 'Import complete! All items already up to date.');
            await fetchAll();
          } catch (err) {
            console.error('Import failed', err);
            alert(`Import failed: ${err.response?.data?.message || err.message || 'Unknown error'}`);
          } finally {
            setImporting(false);
            e.target.value = '';
          }
        }}
      />
      <PageHeader
        title="Finishes"
        subtitle="Manage finish groups, types, and colors"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<FileDownloadRounded />} onClick={async () => {
              try {
                const { items: rows } = await exportFinishesJson();
                await exportRowsToXlsx({
                  rows,
                  columns: ['_id', 'group', 'type', 'name', 'image_url', 'color_code', 'internal_reference'],
                  filename: 'finishes',
                  sheetName: 'Finishes',
                });
              } catch (err) {
                console.error('Export failed:', err);
                alert('Export failed.');
              }
            }}>
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => document.getElementById('finishes-import-file')?.click()}
              disabled={importing}
            >
              {importing ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => { setFormName(''); setError(''); setGroupDialog(true); }}>
              Add Group
            </Button>
          </Box>
        }
      />

      {/* ─── Toolbar / Filter Bar ─── */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            size="small"
            options={allManufacturers}
            getOptionLabel={(o) => o.name}
            value={filterManufacturer}
            onChange={(_, val) => setFilterManufacturer(val)}
            renderInput={(params) => <TextField {...params} label="Manufacturer" />}
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            label="Store"
            value={filterStore}
            onChange={(e) => { setFilterStore(e.target.value); if (!e.target.value) setFilterStatus('All'); }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Stores</MenuItem>
            {activeStores.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {storeLabel(s)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            disabled={!filterStore}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Published">Published</MenuItem>
            <MenuItem value="Unpublished">Unpublished</MenuItem>
          </TextField>
        </Box>
      </Card>

      {/* ─── Group Bulk Actions Bar ─── */}
      {selectedGroups.size > 0 && (
        <Card sx={{ mb: 2, p: 1.5, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Checkbox
              size="small"
              checked={groups.length > 0 && groups.every((g) => selectedGroups.has(g.id))}
              indeterminate={selectedGroups.size > 0 && !groups.every((g) => selectedGroups.has(g.id))}
              onChange={handleToggleSelectAllGroups}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedGroups.size} group{selectedGroups.size !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={bulkActioning ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRounded />}
              disabled={bulkActioning}
              onClick={handleBulkDeleteGroups}
            >
              Delete Selected Groups
            </Button>
            <Button size="small" variant="outlined" onClick={() => setSelectedGroups(new Set())}>
              Clear
            </Button>
          </Box>
        </Card>
      )}

      {/* ─── Type Bulk Actions Bar ─── */}
      {selectedTypes.size > 0 && (
        <Card sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Checkbox
              size="small"
              checked={allVisibleSelected}
              indeterminate={selectedTypes.size > 0 && !allVisibleSelected}
              onChange={handleToggleSelectAll}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedTypes.size} type{selectedTypes.size !== 1 ? 's' : ''} selected
            </Typography>
            <TextField
              select
              size="small"
              label="Store"
              value={bulkStore}
              onChange={(e) => setBulkStore(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {activeStores.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {storeLabel(s)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="contained"
              startIcon={bulkActioning ? <CircularProgress size={16} color="inherit" /> : <PublishRounded />}
              disabled={!bulkStore || bulkActioning}
              onClick={handleBulkPush}
            >
              Push to Shopify
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<UnpublishedRounded />}
              disabled={!bulkStore || bulkActioning}
              onClick={() => handleBulkAction('inactive')}
            >
              Unpublish
            </Button>
            <Button size="small" variant="outlined" onClick={() => setSelectedTypes(new Set())}>
              Clear selection
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteOutlineRounded />}
              disabled={bulkActioning}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
            {bulkActioning && <CircularProgress size={20} />}
          </Box>
        </Card>
      )}

      {/* Column header row — sticky above the finish group cards so the
          per-store status Selects on each type row sit under a labeled
          column instead of looking like floating identical dropdowns.
          Right-side slot widths must match what the type row uses. */}
      {groups.length > 0 && activeStores.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            mb: 1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem' }}>
            Type
          </Typography>
          {activeStores.map((store) => (
            <Box key={store.id} sx={{ width: 110, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                {storeLabel(store)}
              </Typography>
            </Box>
          ))}
          <Box sx={{ width: 132 }} />{/* actions column spacer */}
        </Box>
      )}

      {groups.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <FormatPaintRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No finish groups added</Typography>
            <Typography variant="body2" color="text.secondary">Click "Add Group" to create your first finish group</Typography>
          </Box>
        </Card>
      ) : (
        groups.map((group) => {
          const isExpanded = expandedGroups[group.id];
          const groupTypes = typesForGroup(group.id);
          // When a filter is active, hide groups with no matching types so the
          // list only shows relevant results. Unfiltered, keep empty groups
          // visible (so you can still add types to them).
          const hasActiveFilter = searchQuery.trim() !== '' || filterManufacturer !== null || filterStore !== '';
          if (hasActiveFilter && groupTypes.length === 0) return null;
          return (
            <Card key={group.id} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => toggleGroup(group.id)}>
                <Checkbox
                  size="small"
                  checked={groupTypes.length > 0 && groupTypes.every((t) => selectedTypes.has(t.id))}
                  indeterminate={groupTypes.some((t) => selectedTypes.has(t.id)) && !groupTypes.every((t) => selectedTypes.has(t.id))}
                  onChange={() => handleToggleGroupSelect(group.id)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.25, mr: 0.5 }}
                />
                <IconButton size="small" sx={{ mr: 1 }}>
                  {isExpanded ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{group.name}</Typography>
                <Chip label={`${groupTypes.length} types`} size="small" sx={{ mr: 1 }} />
                <Tooltip title="Rename Group">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditGroup(group); }} sx={{ color: 'primary.main', mr: 0.5 }}>
                    <EditRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add Type">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFormName(''); setError(''); setTypeDialog({ open: true, groupId: group.id }); }} sx={{ color: 'primary.main', mr: 0.5 }}>
                    <AddRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Group">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }} disabled={deleting === `group-${group.id}`} sx={{ color: 'error.main' }}>
                    {deleting === `group-${group.id}` ? <CircularProgress size={16} /> : <DeleteOutlineRounded fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={isExpanded}>
                {groupTypes.length === 0 ? (
                  <Box sx={{ px: 4, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">No types yet. Click + to add one.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ px: 1, pb: 1 }}>
                    {groupTypes.map((type) => {
                      const typeExpanded = expandedTypes[type.id];
                      const colors = colorsByType[type.id] || [];
                      return (
                        <Box key={type.id} sx={{ ml: 2, borderLeft: '2px solid', borderColor: 'divider', pl: 1.5, mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.75 }}>
                            <Checkbox
                              size="small"
                              checked={selectedTypes.has(type.id)}
                              onChange={() => handleToggleSelect(type.id)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ mr: 0.5, p: 0.25 }}
                            />
                            <IconButton size="small" onClick={() => toggleType(type.id)} sx={{ mr: 0.5 }}>
                              {typeExpanded ? <ExpandMoreRounded fontSize="small" /> : <ChevronRightRounded fontSize="small" />}
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{type.name}</Typography>
                            {type.internal_reference && <Chip label={type.internal_reference} size="small" variant="outlined" sx={{ fontSize: '0.7rem', mr: 1 }} />}
                            {type.manufacturers.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                                {type.manufacturers.map((m) => <Chip key={m.id} label={m.name} size="small" sx={{ fontSize: '0.65rem' }} />)}
                              </Box>
                            )}
                            <Chip label={`${type.color_count} colors`} size="small" sx={{ mr: 1, fontSize: '0.7rem' }} />

                            {/* Per-store Shopify status — one fixed-width column per
                                active store, lined up under the sticky header above.
                                Linked → editable Select; unlinked → grey chip.
                                Widths MUST match the header row (110px per store). */}
                            {activeStores.map((store) => {
                              const link = (type.store_statuses || []).find((s) => s.store_id === store.id);
                              const isLinked = !!link?.shopify_product_id;
                              const shopifyStatus = link?.shopify_status || 'unknown';
                              const key = `${type.id}-${store.id}`;
                              return (
                                <Box key={store.id} sx={{ width: 110, display: 'flex', justifyContent: 'center' }}>
                                  {isLinked ? (
                                    <FormControl size="small" sx={{ width: 100 }}>
                                      <Select
                                        value={['active','draft','archived'].includes(shopifyStatus) ? shopifyStatus : ''}
                                        onChange={(e) => { e.stopPropagation(); handleShopifyStatusChangeForRow(type.id, store.id, e.target.value); }}
                                        onClick={(e) => e.stopPropagation()}
                                        displayEmpty
                                        disabled={updatingStatus === key}
                                        sx={{ fontSize: '0.7rem', height: 26, '& .MuiSelect-select': { py: 0.25, pl: 1 } }}
                                      >
                                        <MenuItem value="" disabled><em>Status</em></MenuItem>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="draft">Draft</MenuItem>
                                        <MenuItem value="archived">Archived</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Chip
                                      label="Not linked"
                                      size="small"
                                      sx={{ fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: 'rgba(148,163,184,0.12)', color: '#94A3B8' }}
                                    />
                                  )}
                                </Box>
                              );
                            })}

                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditType(type.id)} sx={{ color: 'primary.main' }}><EditRounded fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Add Color"><IconButton size="small" onClick={() => { setFormName(''); setFormImage(''); setFormColorCode(''); setError(''); setColorDialog({ open: true, typeId: type.id }); }} sx={{ color: 'primary.main' }}><AddRounded fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteType(type.id)} disabled={deleting === `type-${type.id}`} sx={{ color: 'error.main' }}>
                              {deleting === `type-${type.id}` ? <CircularProgress size={14} /> : <DeleteOutlineRounded fontSize="small" />}
                            </IconButton></Tooltip>
                          </Box>

                          <Collapse in={typeExpanded}>
                            <Box sx={{ pl: 4, pb: 1 }}>
                              {colors.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>No colors yet</Typography>
                              ) : (
                                colors.map((color) => (
                                  <Box key={color.id} sx={{ display: 'flex', alignItems: 'center', py: 0.5, gap: 1.5 }}>
                                    <Avatar variant="rounded" src={color.image_url} sx={{ width: 28, height: 28, bgcolor: 'grey.100' }}>
                                      <ImageRounded sx={{ fontSize: 14, color: 'grey.400' }} />
                                    </Avatar>
                                    <Typography variant="body2" sx={{ flex: 1 }}>{color.name}</Typography>
                                    {color.color_code && (
                                      <Chip label={color.color_code} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {color.image_url || '--'}
                                    </Typography>
                                    <Tooltip title="Edit Color"><IconButton size="small" onClick={() => openEditColor(color, type.id)} sx={{ color: 'primary.main' }}>
                                      <EditRounded sx={{ fontSize: 14 }} />
                                    </IconButton></Tooltip>
                                    <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteColor(color.id, type.id)} disabled={deleting === `color-${color.id}`} sx={{ color: 'error.main' }}>
                                      {deleting === `color-${color.id}` ? <CircularProgress size={14} /> : <DeleteOutlineRounded fontSize="small" />}
                                    </IconButton></Tooltip>
                                  </Box>
                                ))
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Collapse>
            </Card>
          );
        })
      )}

      {/* Add Group Dialog */}
      <Dialog open={groupDialog} onClose={() => setGroupDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Finish Group</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Group Name" placeholder="e.g. Wood" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!error} helperText={error} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGroupDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateGroup} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editGroupDialog.open} onClose={() => setEditGroupDialog({ open: false, group: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Rename Finish Group</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Group Name" value={groupEditName}
            onChange={(e) => { setGroupEditName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!error} helperText={error} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditGroupDialog({ open: false, group: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGroupEdit} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Type Dialog */}
      <Dialog open={typeDialog.open} onClose={() => setTypeDialog({ open: false, groupId: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Finish Type</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Type Name" placeholder="e.g. Oak" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!error} helperText={error} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTypeDialog({ open: false, groupId: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateType} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Color Dialog */}
      <Dialog open={colorDialog.open} onClose={() => setColorDialog({ open: false, typeId: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Color</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Color Name" placeholder="e.g. Natural" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error} />
          <TextField label="Color Code" placeholder="e.g. #FF5733 or RAL 9010" value={formColorCode}
            onChange={(e) => setFormColorCode(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <ImageUploader value={formImage} onChange={setFormImage} folder="finish-colors" label="Color Image" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setColorDialog({ open: false, typeId: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateColor} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Color Dialog */}
      <Dialog open={editColorDialog.open} onClose={() => setEditColorDialog({ open: false, color: null, typeId: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Color</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Color Name" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error} />
          <TextField label="Color Code" placeholder="e.g. #FF5733 or RAL 9010" value={formColorCode}
            onChange={(e) => setFormColorCode(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <ImageUploader value={formImage} onChange={setFormImage} folder="finish-colors" label="Color Image" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditColorDialog({ open: false, color: null, typeId: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveColor} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Type Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, type: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Finish Type — {editDialog.type?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
            <TextField label="Type Name" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Internal Reference" value={editForm.internal_reference || ''} onChange={(e) => setEditForm((f) => ({ ...f, internal_reference: e.target.value }))} fullWidth placeholder="e.g. WD-OAK-01" />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField select label="Group" value={editForm.finish_group_id || ''} onChange={(e) => setEditForm((f) => ({ ...f, finish_group_id: e.target.value }))} fullWidth>
              {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={editForm.status || 'ready'} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))} fullWidth>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="needs_attention">Needs Attention</MenuItem>
            </TextField>
          </Box>

          <Autocomplete multiple size="small" options={allManufacturers} getOptionLabel={(o) => o.name}
            value={allManufacturers.filter((m) => editMfrs.includes(m.id))}
            onChange={(_, val) => setEditMfrs(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Manufacturers" />} sx={{ mb: 2 }} />

          <Autocomplete multiple size="small" options={allFeatures} getOptionLabel={(o) => o.name}
            value={allFeatures.filter((f) => editFeatures.includes(f.id))}
            onChange={(_, val) => setEditFeatures(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Features" placeholder="Select features (sent as tags)" />} sx={{ mb: 2 }} />

          {/* Store Status Toggles */}
          {activeStores.length > 0 && editDialog.type && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Store Connections</Typography>
              {activeStores.map((store) => {
                const ss = editStoreStatuses[store.id];
                const storeStatus = typeof ss === 'string' ? ss : ss?.status || 'inactive';
                const isActive = storeStatus === 'active';
                const isLinked = typeof ss === 'object' && !!ss?.shopify_product_id;
                const shopifyStatus = typeof ss === 'object' ? ss?.shopify_status || 'unknown' : 'unknown';

                return (
                  <Box key={store.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                    <Chip
                      label={storeLabel(store)}
                      color={isActive ? 'success' : 'default'} variant={isActive ? 'filled' : 'outlined'}
                      onClick={() => {
                        setEditStoreStatuses((prev) => ({
                          ...prev,
                          [store.id]: typeof prev[store.id] === 'object'
                            ? { ...prev[store.id], status: isActive ? 'inactive' : 'active' }
                            : isActive ? 'inactive' : 'active',
                        }));
                      }}
                      size="small" sx={{ cursor: 'pointer' }}
                    />
                    <Box sx={{ flex: 1 }}>
                      {isLinked ? (
                        <Typography variant="caption" color="text.secondary">Shopify #{ss.shopify_product_id}</Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Not linked</Typography>
                      )}
                    </Box>
                    {isLinked && (
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={shopifyStatus !== 'unknown' ? shopifyStatus : ''}
                          onChange={(e) => handleShopifyStatusChange(editDialog.type.id, store.id, e.target.value)}
                          displayEmpty disabled={updatingStatus === store.id}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          <MenuItem value="" disabled><em>Status</em></MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {isLinked ? (
                      <Button size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem', minWidth: 0, px: 1 }}
                        onClick={() => handleUnmatch(editDialog.type.id, store.id)}>Unlink</Button>
                    ) : (
                      <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', minWidth: 0, px: 1 }}
                        onClick={() => openMatchForType(editDialog.type.id, store.id)}>Match</Button>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog({ open: false, type: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Match Shopify Product Dialog */}
      <Dialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Match with Shopify Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <TextField size="small" label="Search by title" value={matchQuery}
              onChange={(e) => setMatchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleMatchSearch()} fullWidth />
            <Button variant="contained" onClick={handleMatchSearch} disabled={matchSearching || !matchQuery.trim()} sx={{ minWidth: 100 }}>
              {matchSearching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
          </Box>
          {matchResults.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {matchResults.map((sp) => (
                <Box key={sp.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleMatch(sp.id)}>
                  {sp.image && <Box component="img" src={sp.image} alt={sp.title} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sp.title}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {sp.id} {sp.status ? `— ${sp.status}` : ''}</Typography>
                  </Box>
                  {matchingProduct && <CircularProgress size={20} />}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMatchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <PushResultsDialog
        open={pushResults.open}
        onClose={() => setPushResults({ open: false, results: [] })}
        title="Finish push results"
        results={pushResults.results}
      />
    </Box>
  );
};

export default Finishes;
